<template>
  <section id="try-consult" class="portal-consult">
    <div class="portal-consult-inner">
      <p class="portal-consult-eyebrow">Clinical Reference</p>
      <h2>Ask a clinical question</h2>
      <p class="portal-consult-sub">
        Same clinical agent as the signed-in Learning Hub — this preview is Quick mode only,
        with a daily question cap. Create an account for unlimited consults, CE courses, and saved history.
      </p>

      <form class="portal-consult-form" @submit.prevent="askDemo">
        <textarea
          v-model="query"
          class="portal-consult-textarea"
          rows="3"
          maxlength="500"
          placeholder="e.g. Meloxicam dose for a 10 kg dog with osteoarthritis"
          :disabled="loading"
        />
        <div class="portal-consult-actions">
          <span class="portal-consult-counter">{{ query.length }} / 500</span>
          <button
            type="submit"
            class="portal-btn portal-btn-primary"
            :disabled="!canSubmit"
          >
            {{ loading ? 'Working…' : 'Ask the agent' }}
          </button>
        </div>
        <div
          v-if="turnstile.siteKey"
          ref="turnstileContainer"
          class="portal-consult-turnstile"
        />
      </form>

      <div v-if="loading && progressSteps.length" class="portal-consult-progress" aria-live="polite">
        <div
          v-for="(step, idx) in progressSteps"
          :key="step.key"
          class="portal-consult-progress-step"
          :class="{
            'is-done': step.done,
            'is-active': !step.done && idx === activeProgressIndex,
          }"
        >
          <i
            v-if="step.done"
            class="fas fa-check-circle"
          />
          <i
            v-else-if="idx === activeProgressIndex"
            class="fas fa-circle-notch fa-spin"
          />
          <i
            v-else
            class="far fa-circle"
          />
          <span>{{ step.message }}</span>
        </div>
      </div>

      <div v-if="error" class="portal-consult-error">
        {{ error }}
      </div>

      <div v-if="errorIsLimit" class="portal-consult-signup">
        <h3>Create a free account to keep going</h3>
        <p>
          Unlimited clinical questions plus {{ shortName }} member CE benefits after you sign in.
        </p>
        <OAuthButtons
          :organization-slug="orgSlug || 'colibri-media-group'"
          @success="onSignupSuccess"
        />
        <p class="portal-consult-signup-foot">
          Already a member?
          <router-link :to="loginPath">Sign in</router-link>
          ·
          <router-link :to="registerPath">Create account</router-link>
        </p>
      </div>

      <div v-if="answer" class="portal-consult-answer">
        <h3>Sample answer</h3>
        <div class="portal-consult-answer-body" v-html="formattedAnswer" />

        <div v-if="sources.length" class="portal-consult-sources">
          <h4>
            <i class="fas fa-flask" aria-hidden="true" />
            {{ sources.length }} research source{{ sources.length === 1 ? '' : 's' }}
          </h4>
          <div
            v-for="(src, i) in visibleSources"
            :key="'src-' + (src.pmid || i)"
            class="portal-consult-source"
          >
            <a
              :href="sourceHref(src)"
              target="_blank"
              rel="noopener noreferrer"
            >
              [{{ i + 1 }}] {{ src.title }}
            </a>
            <span v-if="src.journal || src.year" class="portal-consult-source-meta">
              {{ [src.journal, src.year].filter(Boolean).join(' · ') }}
            </span>
          </div>
          <button
            v-if="sources.length > 3"
            type="button"
            class="portal-consult-more"
            @click="showAllSources = !showAllSources"
          >
            {{ showAllSources ? 'Show fewer sources' : `See ${sources.length - 3} more sources` }}
          </button>
        </div>

        <div v-if="hasTeasers" class="portal-consult-teasers">
          <h4>Related CE (unlock with a free account)</h4>
          <ul>
            <li v-for="(course, idx) in teasers.courses.slice(0, 3)" :key="'c-' + idx">
              <i class="fas fa-lock" aria-hidden="true" />
              {{ course.title }}
            </li>
            <li v-for="(video, idx) in teasers.videos.slice(0, 2)" :key="'v-' + idx">
              <i class="fas fa-lock" aria-hidden="true" />
              {{ video.title }}
            </li>
          </ul>
        </div>

        <div class="portal-consult-cta-row">
          <router-link :to="registerPath" class="portal-btn portal-btn-primary">
            Create free account
          </router-link>
          <router-link :to="loginPath" class="portal-btn portal-btn-outline">
            Member Sign In
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import OAuthButtons from '@/components/OAuthButtons.vue'
import { useTurnstile } from '@/composables/useTurnstile'
import { renderConsultMarkdown } from '@/utils/consultMarkdown'
import {
  learningHomeLocation,
  loginPathForOrg,
  registerPathForOrg,
} from '@/utils/associationPortal'

const DEMO_PROGRESS_STEPS = [
  { key: 'deploying', message: 'Deploying agents…' },
  { key: 'searching', message: 'Searching veterinary literature…' },
  { key: 'scoring', message: 'Scoring evidence quality…' },
  { key: 'synthesizing', message: 'Writing a cited clinical answer…' },
]

const PROGRESS_STEP_MS = 2200

const props = defineProps({
  orgSlug: {
    type: String,
    default: '',
  },
  shortName: {
    type: String,
    default: 'Member',
  },
})

const router = useRouter()
const query = ref('')
const loading = ref(false)
const answer = ref('')
const sources = ref([])
const teasers = ref({ courses: [], videos: [] })
const error = ref('')
const errorIsLimit = ref(false)
const showAllSources = ref(false)
const progressSteps = ref([])
const activeProgressIndex = ref(0)
let progressTimer = null

const turnstileContainer = ref(null)
const turnstile = useTurnstile({ containerRef: turnstileContainer })

const loginPath = computed(() => loginPathForOrg(props.orgSlug))
const registerPath = computed(() => registerPathForOrg(props.orgSlug))
const formattedAnswer = computed(() => renderConsultMarkdown(answer.value, sources.value))
const visibleSources = computed(() => (
  showAllSources.value ? sources.value : sources.value.slice(0, 3)
))
const hasTeasers = computed(() => (
  (teasers.value.courses && teasers.value.courses.length > 0)
  || (teasers.value.videos && teasers.value.videos.length > 0)
))
const canSubmit = computed(() => {
  if (loading.value) return false
  if (query.value.trim().length < 5) return false
  return turnstile.canSubmit()
})

onMounted(async () => {
  await nextTick()
  await turnstile.mount()
})

function stopProgress() {
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
}

function startProgress() {
  stopProgress()
  progressSteps.value = DEMO_PROGRESS_STEPS.map((s) => ({ ...s, done: false }))
  activeProgressIndex.value = 0
  progressTimer = setInterval(() => {
    if (activeProgressIndex.value >= progressSteps.value.length - 1) return
    progressSteps.value[activeProgressIndex.value].done = true
    activeProgressIndex.value += 1
  }, PROGRESS_STEP_MS)
}

function clearProgress() {
  stopProgress()
  progressSteps.value = []
  activeProgressIndex.value = 0
}

function completeProgress() {
  stopProgress()
  progressSteps.value = progressSteps.value.map((s) => ({ ...s, done: true }))
  activeProgressIndex.value = Math.max(progressSteps.value.length - 1, 0)
}

function sourceHref(src) {
  if (src?.url) return src.url
  const pmid = src?.pmid || ''
  if (/^\d+$/.test(String(pmid))) return `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`
  return '#'
}

async function askDemo() {
  if (!canSubmit.value) return
  loading.value = true
  error.value = ''
  errorIsLimit.value = false
  answer.value = ''
  sources.value = []
  teasers.value = { courses: [], videos: [] }
  showAllSources.value = false
  startProgress()

  try {
    const apiBase = (process.env.VUE_APP_API_URL || '/api').replace(/\/$/, '')
    const resp = await fetch(`${apiBase}/vet-consult/public-demo/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        query: query.value.trim(),
        turnstile_token: turnstile.token.value,
      }),
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      clearProgress()
      error.value = data.message || 'We could not generate a sample answer. Please try again.'
      errorIsLimit.value = resp.status === 429
      return
    }
    completeProgress()
    answer.value = data.answer || ''
    sources.value = data.sources || []
    teasers.value = data.teasers || { courses: [], videos: [] }
    await new Promise((r) => setTimeout(r, 300))
  } catch {
    clearProgress()
    error.value = 'Network error. Please check your connection and retry.'
  } finally {
    loading.value = false
    clearProgress()
    turnstile.reset()
  }
}

function onSignupSuccess() {
  const location = learningHomeLocation(props.orgSlug)
  const query = { ...(location.query || {}) }
  if (props.orgSlug) {
    query.org = props.orgSlug
  }
  router.push({ path: location.path, query })
}
</script>

<style scoped>
.portal-consult {
  padding: 0 0 2.5rem;
}

.portal-consult-inner {
  width: min(760px, calc(100% - 2rem));
  margin: 0 auto;
  padding: 1.75rem;
  border-radius: 16px;
  background: var(--portal-surface, #fff);
  border: 1px solid color-mix(in srgb, var(--portal-primary) 12%, #e5e7eb);
  box-shadow: 0 12px 32px color-mix(in srgb, var(--portal-primary) 8%, transparent);
}

.portal-consult-eyebrow {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--portal-secondary);
}

.portal-consult h2 {
  margin: 0 0 0.65rem;
  font-size: clamp(1.45rem, 3vw, 1.85rem);
  color: var(--portal-primary);
}

.portal-consult-sub {
  margin: 0 0 1.25rem;
  line-height: 1.55;
  color: color-mix(in srgb, var(--portal-text, #0f1f3d) 72%, #6b7280);
}

.portal-consult-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  font: inherit;
  resize: vertical;
  min-height: 96px;
}

.portal-consult-textarea:focus {
  outline: 2px solid color-mix(in srgb, var(--portal-primary) 45%, transparent);
  border-color: var(--portal-primary);
}

.portal-consult-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.portal-consult-counter {
  color: #94a3b8;
  font-size: 0.85rem;
}

.portal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.2rem;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
}

.portal-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.portal-btn-primary {
  background: var(--portal-primary);
  color: #fff;
}

.portal-btn-outline {
  border: 1px solid color-mix(in srgb, var(--portal-primary) 24%, #cbd5e1);
  color: var(--portal-primary);
  background: #fff;
}

.portal-consult-turnstile {
  margin-top: 0.85rem;
  min-height: 65px;
}

.portal-consult-progress {
  margin-top: 1rem;
  display: grid;
  gap: 0.45rem;
}

.portal-consult-progress-step {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: #64748b;
  font-size: 0.92rem;
}

.portal-consult-progress-step.is-active,
.portal-consult-progress-step.is-done {
  color: var(--portal-primary);
}

.portal-consult-error {
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #fef2f2;
  color: #991b1b;
}

.portal-consult-signup,
.portal-consult-answer {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
}

.portal-consult-signup h3,
.portal-consult-answer h3 {
  margin: 0 0 0.5rem;
  color: var(--portal-primary);
}

.portal-consult-signup-foot {
  margin-top: 0.85rem;
  color: #64748b;
  font-size: 0.92rem;
}

.portal-consult-answer-body {
  line-height: 1.65;
}

.portal-consult-sources {
  margin-top: 1.25rem;
}

.portal-consult-sources h4,
.portal-consult-teasers h4 {
  margin: 0 0 0.65rem;
  font-size: 1rem;
  color: var(--portal-primary);
}

.portal-consult-source {
  display: grid;
  gap: 0.15rem;
  margin-bottom: 0.65rem;
}

.portal-consult-source a {
  color: var(--portal-secondary);
  font-weight: 600;
  text-decoration: none;
}

.portal-consult-source-meta {
  color: #64748b;
  font-size: 0.85rem;
}

.portal-consult-more {
  background: none;
  border: none;
  color: var(--portal-primary);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.portal-consult-teasers {
  margin-top: 1.25rem;
}

.portal-consult-teasers ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.portal-consult-teasers li {
  margin-bottom: 0.45rem;
  color: #475569;
}

.portal-consult-teasers i {
  margin-right: 0.4rem;
  color: #94a3b8;
}

.portal-consult-cta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1.25rem;
}
</style>
